import React, { useRef, useCallback, useEffect, useMemo } from "react";
import { Container, Sprite, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useTranslation } from "react-i18next";
import { getNewUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";
import { FONT_CONFIG } from "../../../../pixiSettings";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";

const Checkbox = ({
  label,
  translationKey,
  checked = false,
  onChange,
  x = 0,
  y = 0,
  sizeMultiplier = 0.7, // Same as close button in settings
  labelFontSize, // Font size passed from parent
  labelStyle, // Optional: full text style passed from parent
  containerWidth, // Optional: max width available for label text
  referenceInputWidth = 0, // Input width from CheckboxWithInput for uniform sizing
}) => {
  const { t } = useTranslation();
  const { getResponsiveConfig } = useDimensions();

  // Use translation if translationKey is provided, otherwise use label prop
  const displayLabel = translationKey ? t(translationKey) : label;
  const checkmarkSpriteRef = useRef(null);

  // Get the checkbox textures

  // Shared hover/press hook
  const { buttonRef, handlePointerOver, handlePointerOut, triggerPress, cleanupFilters } =
    useButtonBrightness({ brightness: 1.3 });

  // Clean up on unmount
  useEffect(() => () => cleanupFilters(), [cleanupFilters]);

  const backgroundTexture = getNewUITexture(
    checked
      ? "checkbox_background_active"
      : "checkbox_background"
  );
  const checkmarkTexture = getNewUITexture("checkmark");

  // Get responsive configuration from context (same as close button)
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  // Apply size multiplier to checkbox dimensions
  const checkboxSize = buttonConfig.width * sizeMultiplier;

  // Calculate label position (to the right of checkbox with some padding)
  const labelX = Math.round(checkboxSize * 1.0);
  const labelY = 0;

  // Use the parent-computed perfect font size directly
  const finalLabelFontSize = labelFontSize || 26;

  // Create default label style if not provided
  const dpr = window.devicePixelRatio || 1;
  const defaultLabelStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalLabelFontSize,
        fontWeight: "400",
        fill: 0xffffff,
        align: "left",
        wordWrap: false,
        resolution: dpr,
      }),
    [finalLabelFontSize, dpr]
  );

  // Use provided labelStyle or default
  const textStyle = labelStyle || defaultLabelStyle;

  const handleClick = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);

    // Add press animation to background
    if (buttonRef.current) {
      triggerPress();
    }

    // Call onChange callback
    if (onChange) {
      onChange(!checked);
    }
  }, [checked, onChange, buttonRef, triggerPress]);

  // Don't render if textures not loaded
  if (!backgroundTexture || !checkmarkTexture) {
    return null;
  }

  return (
    <Container
      name="secondary_checkbox"
      x={x}
      y={y}
      eventMode="static"
      cursor="pointer"
      onpointertap={handleClick}
      onpointerover={handlePointerOver}
      onpointerout={handlePointerOut}
    >
      {/* Checkbox Background */}
      <Sprite
        ref={buttonRef}
        texture={backgroundTexture}
        name="secondary_checkbox_background"
        anchor={0.5}
        x={0}
        y={0}
        width={checkboxSize}
        height={checkboxSize}
        roundPixels={true}
      />

      {/* Checkmark (only visible when checked) */}
      {checked && (
        <Sprite
          ref={checkmarkSpriteRef}
          texture={checkmarkTexture}
          name="secondary_checkmark"
          anchor={0.5}
          x={0}
          y={0}
          width={checkboxSize * 0.65} // Wider
          height={checkboxSize * 0.45} // Less tall - makes it more horizontal
          roundPixels={true}
          eventMode="none" // Don't intercept events
        />
      )}

      {/* Label Text */}
      <Text
        text={displayLabel}
        style={textStyle}
        anchor={{ x: 0, y: 0.5 }}
        x={labelX}
        y={labelY}
        roundPixels={true}
        alpha={checked ? 1.0 : 0.6}
        eventMode="none" // Don't intercept events
      />
    </Container>
  );
};

export default Checkbox;
